import React, { useEffect, useState } from 'react';
import { X, LoaderCircle } from 'lucide-react';
import { Button, Chip, Tab } from 'common/components';
import { Contact, formatStatusForDisplay } from 'types/contact.type';
import { useGetContactDetails } from 'services/hooks/mutations/useGetContactDetails';
import {
    calculateDuration,
    processStatusHistory,
    ProcessedStatusHistory,
    ProcessedHistory,
    getWorkflowPartyStages,
} from './ContactDrawer.utils';
import { TabDetails, TabChangeStatus, TabStatusHistory } from './components';

import styles from './ContactDrawer.module.scss';

export type {
    ProcessedCallHistory,
    ProcessedSMSHistory,
    ProcessedHistory,
    ProcessedWorkflowHistoryGroup,
} from './ContactDrawer.utils';

export interface ContactDrawerProps {
    isOpen: boolean;
    contact: Contact | null;
    activeTab: string;
    rawStatusOptions: string[];
    onClose: () => void;
    onTabChange: (tabId: string) => void;
}

const ContactDrawer: React.FC<ContactDrawerProps> = ({
    isOpen,
    contact,
    activeTab,
    rawStatusOptions,
    onClose,
    onTabChange,
}) => {
    const [contactHistory, setContactHistory] = useState<ProcessedHistory[]>([]);
    const [statusHistory, setStatusHistory] = useState<ProcessedStatusHistory[]>([]);
    const [workflowPartyStages, setWorkflowPartyStages] = useState<Record<string, string>>({});
    const { contactDetails, isContactDetailsLoading } = useGetContactDetails({
        id: contact?.contactId,
        isEnabled: !!isOpen && !!contact?.contactId,
    });

    useEffect(() => {
        if (!contactDetails) return;

        const formattedCalls =
            contactDetails?.calls
                ?.map((call) => {
                    return {
                        id: call.id,
                        date: new Date(call.start_time || call.created_at),
                        duration: calculateDuration(call.start_time, call.end_time),
                        status: call.status || 'Unknown',
                        workflow: call.edges?.workflow?.title || call.workflow_name || 'Unknown',
                        workflowId: call.workflow_id || call.edges?.workflow?.id || 'unknown',
                        callDirection: call.call_direction,
                        disposition: call.disposition,
                        aggregatedTagsUsed: call.aggregatedTagsUsed ?? false,
                        type: 'call' as const,
                    };
                })
                .sort((a, b) => b.date.getTime() - a.date.getTime()) || [];

        const formatedSMS =
            contactDetails?.sms?.map((sms) => {
                return {
                    id: sms.smsId,
                    date: new Date(sms.sentTime),
                    message: sms.messageContent,
                    status: sms.status || 'Unknown',
                    workflow: sms.workflowName || 'Unknown',
                    workflowId: sms.workflowId || 'unknown',
                    messageDirection: sms.messageDirection,
                    disposition: sms.disposition,
                    type: 'sms' as const,
                };
            }) || [];

        const formattedHistory = [...formattedCalls, ...formatedSMS].sort(
            (a, b) => b.date.getTime() - a.date.getTime()
        );

        setContactHistory(formattedHistory);

        setStatusHistory(processStatusHistory(contactDetails.history));
        setWorkflowPartyStages(getWorkflowPartyStages(contactDetails.history));

        return () => {
            setContactHistory([]);
            setStatusHistory([]);
            setWorkflowPartyStages({});
        };
    }, [contactDetails]);

    return (
        <>
            {isOpen && (
                <div className={styles.drawerOverlay} onClick={onClose} data-testid='contact-drawer-overlay'></div>
            )}
            <div className={`${styles.drawer} ${isOpen ? styles.open : ''}`}>
                <div className={styles.drawerHeader}>
                    <div className='flex items-center gap-sm'>
                        <h3>{contact?.contactName}</h3>
                        <Chip color='primary'>{formatStatusForDisplay(contact?.status || '')}</Chip>
                    </div>
                    <Button
                        variant='ghost'
                        size='sm'
                        icon={X}
                        iconOnly
                        onClick={onClose}
                        className={styles.drawerCloseButton}
                        aria-label='Close drawer'
                    />
                </div>
                <div className={styles.drawerTabs}>
                    <Tab
                        variant='buttonStyle'
                        tabs={[
                            { id: 'details', label: 'Contact History' },
                            { id: 'history', label: 'Status History' },
                            { id: 'change', label: 'Change Status' },
                        ]}
                        activeTab={activeTab}
                        onTabChange={onTabChange}
                    />
                </div>
                <div className={styles.drawerContent}>
                    {isContactDetailsLoading && (
                        <div className={styles.loadingOverlay}>
                            <LoaderCircle size={26} className={styles.spinner} color='var(--primary-purple)' /> Fetching
                            contact details...
                        </div>
                    )}
                    {activeTab === 'details' && (
                        <TabDetails
                            contactHistory={contactHistory}
                            workflowPartyStages={workflowPartyStages}
                            contactName={contact?.contactName}
                        />
                    )}

                    {activeTab === 'history' && (
                        <TabStatusHistory statusHistory={statusHistory} workflowPartyStages={workflowPartyStages} />
                    )}

                    {activeTab === 'change' && contact && (
                        <TabChangeStatus rawStatusOptions={rawStatusOptions} contact={contact} />
                    )}
                </div>
            </div>
        </>
    );
};

export default ContactDrawer;
